/**
 * TransformCommand
 *
 * Captures before/after transform snapshots (position, rotation, scale).
 * Used for both gizmo drags and UI input field changes.
 * Supports coalescing so rapid keystrokes merge into a single undo entry.
 */

import type { UndoCommand, SerializedCommand, UndoOptions } from './UndoCommand';
import type { SceneObject } from '../../core/SceneObject';
import type { SceneObjectRef } from '../../core/SceneObjectIdentity';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import { Properties } from '../../behaviors/properties';
import { registerCommandCodec } from './CommandCodec';

const CODEC_TYPE = 'Transform';

interface TransformPayload {
    objectPath: string;
    before: TransformSnapshot;
    after: TransformSnapshot;
}

export interface TransformSnapshot {
    position: [number, number, number];
    rotation: [number, number, number, number]; // quaternion [x, y, z, w]
    scale: [number, number, number];
}

/** Coalesce window for rapid transform changes on the same object */
const COALESCE_WINDOW_MS = 500;

export class TransformCommand implements UndoCommand {
    readonly label: string;
    /** Shared identity ref — survives Create/Delete undo cycles. */
    private identity: SceneObjectRef;
    private before: TransformSnapshot;
    private after: TransformSnapshot;
    private timestamp: number;
    private pathAtConstruction: string;

    constructor(sceneObject: SceneObject, before: TransformSnapshot, after: TransformSnapshot) {
        this.identity = sceneObject.traceScene.objectIdentityRegistry.refFor(sceneObject);
        this.before = before;
        this.after = after;
        this.timestamp = Date.now();
        this.pathAtConstruction = sceneObject.getPath();

        this.label = `Transform ${sceneObject.name}`;
    }

    undo(options?: UndoOptions): void {
        const obj = this.identity.get();
        if (!obj) return;
        applySnapshot(obj, this.before);
        if (!options?.silent) obj.traceScene?.select(obj);
    }

    redo(): void {
        const obj = this.identity.get();
        if (!obj) return;
        applySnapshot(obj, this.after);
        obj.traceScene?.select(obj);
    }

    serialize(): SerializedCommand {
        const obj = this.identity.get();
        const payload: TransformPayload = {
            objectPath: obj ? obj.getPath() : this.pathAtConstruction,
            before: structuredClone(this.before),
            after: structuredClone(this.after),
        };
        return { type: CODEC_TYPE, data: payload };
    }

    coalesce(other: UndoCommand): boolean {
        if (!(other instanceof TransformCommand)) return false;
        if (other.identity !== this.identity) return false;
        if (other.timestamp - this.timestamp > COALESCE_WINDOW_MS) return false;

        // Keep our original `before`, take their `after`. The timestamp
        // update implements a sliding window — continuous gizmo drags
        // keep merging into a single undo entry as long as consecutive
        // samples fall within COALESCE_WINDOW_MS of each other. Pausing
        // longer than the window breaks the chain.
        this.after = other.after;
        this.timestamp = other.timestamp;
        return true;
    }

    /** Capture current transform state from a SceneObject, in either rotation mode. */
    static capture(sceneObject: SceneObject): TransformSnapshot {
        const t = sceneObject.transform;
        const q = t.rotationQuaternion ?? Quaternion.FromEulerVector(t.rotation);
        return {
            position: [t.position.x, t.position.y, t.position.z],
            rotation: [q.x, q.y, q.z, q.w],
            scale: [t.scaling.x, t.scaling.y, t.scaling.z],
        };
    }
}

/** Put a snapshot back on an object and fold it into its authored data. */
export function applySnapshot(obj: SceneObject, snap: TransformSnapshot): void {
    const t = obj.transform;
    t.position.set(snap.position[0], snap.position[1], snap.position[2]);
    if (!t.rotationQuaternion) {
        t.rotationQuaternion = new Quaternion(snap.rotation[0], snap.rotation[1], snap.rotation[2], snap.rotation[3]);
    } else {
        t.rotationQuaternion.set(snap.rotation[0], snap.rotation[1], snap.rotation[2], snap.rotation[3]);
    }
    t.scaling.set(snap.scale[0], snap.scale[1], snap.scale[2]);
    t.computeWorldMatrix(true);

    // Reset (like the gizmo drag path) so captureTransform folds the just-set
    // snapshot above into authored sceneData, not Properties' stale cached pose.
    obj.getBehavior(Properties)?.resetSavedTransform();
    obj.captureTransform();
}

registerCommandCodec(CODEC_TYPE, (data, scene): TransformCommand => {
    const p = data as TransformPayload;
    const obj = scene.resolveSceneObject(p.objectPath);
    if (!obj) {
        throw new Error(`[TransformCommand] Cannot resolve path "${p.objectPath}"`);
    }
    return new TransformCommand(obj, p.before, p.after);
});
